import React, { useContext } from "react"
import { GetServerSideProps } from "next"
import { styled } from "@mui/material/styles"

import { Grid, Box, Container, LinearProgress, linearProgressClasses } from "@mui/material"
import { makeStyles } from "makeStyles"

import { getLineItemTaxRate } from "@secureo/common/utils/tax"

import NotFoundPage from "pages/404"
import SEO from "@secureo/common/components/SEO"
import Link from "components/i18n/Link"
import BreadCrumbs, { BreadCrumb } from "components/BreadCrumbs"
import LazyLoadedImage from "components/LazyLoadedImage"
import Price from "components/Price"
import Button from "components/Buttons/Button"
import ImageSlider from "containers/ProductDetailPage/ImageSlider"

import AppContext from "@secureo/common/context/AppContext/AppContext"
import useToggle from "@secureo/common/hooks/useToggle"

import Icon from "@mdi/react"
import { mdiChevronDown, mdiChevronUp } from "@mdi/js"

import useLockingSystemPreviewProducts from "@secureo/common/hooks/data/useLockingSystemPreviewProducts"

import { anonymousAccessTokenCache } from "@secureo/common/utils/commerceTools/token/accessTokenCache"
import fetchLockingSystemCategory from "@secureo/common/utils/commerceTools/graphql/queries/fetchLockingSystemCategory"
import { netToGross } from "@secureo/common/utils/tax"

import { LockingSystemCategory } from "@secureo/common/typings/Category"
import { CountryCode } from "@secureo/common/typings/CountryCode"
import { CustomAppProps } from "pages/_app"
import { LockingSystemPreviewProduct } from "@secureo/common/typings/LockingSystem"
import { useTranslation } from "react-i18next"
import normalizeLocale from "@secureo/common/utils/normalizeLocale"
import { CurrencyCode } from "@secureo/common/typings/Forex"

const LockingSystemComponent = ({
	component,
	countryCode,
	taxRate,
	name,
}: LockingSystemComponentProps) => {
	const { t } = useTranslation("lockingSystem")

	const { pricesByCountryCode, images, lockingSystemComponentType } = component

	const netPriceCents = pricesByCountryCode?.[countryCode] ?? 0
	const grossPriceCents = Math.round(netToGross(netPriceCents, taxRate))

	return (
		<Box
			py={2}
			mb={1}
			style={{
				boxShadow: "0px 0px 5px 0px rgba(0,0,0,0.15)",
				borderRadius: 5,
				background: "white",
			}}
		>
			<Grid container spacing={3}>
				<Grid item xs={3} sm={2}>
					<LazyLoadedImage
						src={images[0]}
						title={name}
						alt={name}
						style={{
							maxWidth: 90,
							maxHeight: 90,
							marginLeft: 20,
						}}
					/>
				</Grid>
				<Grid item xs={9} sm={10}>
					<Box px={2}>
						<span style={{ fontSize: "0.75rem" }}>{t(lockingSystemComponentType)}</span>
						<h3 style={{ marginTop: 0, marginBottom: 0 }}>{name}</h3>
						<span>
							ab <Price priceInEuroCents={grossPriceCents} />
						</span>
					</Box>
				</Grid>
			</Grid>
		</Box>
	)
}

interface LockingSystemComponentProps {
	component: LockingSystemPreviewProduct
	countryCode: CountryCode
	taxRate: number
	name: string
}

const useStyles = makeStyles()({
	titlePositionMobile: {
		display: "none",
		"@media only screen and (max-width: 960px)": {
			display: "block",
			lineHeight: 1.05,
			fontSize: "90%",
		},
	},
	titlePositionDesktop: {
		display: "block",
		"@media only screen and (max-width: 960px)": {
			display: "none",
		},
	},
})

const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
	height: 10,
	borderRadius: 5,
	[`&.${linearProgressClasses.colorPrimary}`]: {
		backgroundColor: theme.palette.grey[theme.palette.mode === "light" ? 200 : 700],
	},
	[`& .${linearProgressClasses.bar}`]: {
		borderRadius: 5,
		backgroundColor: "#1a90ff",
	},
}))

const LockingSystemPage = ({
	asPath,
	locale,
	countryCode,
	lockingSystemCategory,
	currencyCode = "EUR",
}: Props & CustomAppProps) => {
	const { accessToken } = useContext(AppContext)
	const { classes } = useStyles()
	const [isOpen, toggleIsOpen] = useToggle()
	const { lockingSystemPreviewProductsByTypeBySku } = useLockingSystemPreviewProducts(
		accessToken,
		lockingSystemCategory?.id,
		currencyCode,
	)

	if (!lockingSystemCategory) return <NotFoundPage />

	const {
		name,
		slug,
		securityScore,
		// recommendedAccessorySkus,
		previewBulletPoints,
		lockingSystemType,
		image,
		defaultLockingComponent,
		longSeoText,
		icon,
	} = lockingSystemCategory

	const lockingSystemComponents = Object.values(lockingSystemPreviewProductsByTypeBySku)
		.map((bySku) =>
			Object.values(bySku).map((lockingSystemComponent) => lockingSystemComponent),
		)
		.flat() as LockingSystemPreviewProduct[]
	const sliderImages = [
		image,
		...lockingSystemComponents.map(({ images }) => images?.[0] ?? null).flat(),
	].filter((image) => !!image)

	const shippingOriginationCountryCode =
		defaultLockingComponent?.variants?.[0]?.attributes
			?.shippingOriginationCountryCodesByDestinationCountryCode?.[countryCode] ?? "DE"
	const { taxRate } = getLineItemTaxRate(shippingOriginationCountryCode, countryCode)

	const title = `${lockingSystemCategory.name} Schließanlage`

	const ancestorPaths: BreadCrumb[] = [
		{
			label: "Schließanlagen",
			href: "/locking-system",
		},
		{
			label:
				lockingSystemType === "mechanic"
					? "Mechanische Schließanlagen"
					: "Elektronische Schließanlagen",
			href:
				lockingSystemType === "mechanic"
					? "/locking-system/mechanic"
					: "/locking-system/electronic",
		},
	]

	const currentPath: BreadCrumb = {
		label: name,
		href: `/locking-system/${lockingSystemType}`,
	}

	return (
		<>
			<SEO asPath={asPath} title={title} appendLocalizedShopNameWithPipe />
			<BreadCrumbs locale={locale} currentPath={currentPath} ancestors={ancestorPaths} />

			<Container>
				<Grid container spacing={3}>
					<Grid item xs={12} md={4}>
						<Box mt={2} className={classes.titlePositionMobile}>
							<h1>{title}</h1>
							<p>Sicherheitslevel: {securityScore} / 10</p>
						</Box>

						<div style={{ position: "relative" }}>
							<ImageSlider images={sliderImages} />
						</div>
					</Grid>

					<Grid item xs={12} md={8}>
						<Grid container spacing={3}>
							<Grid item xs={12} lg={12}>
								<Box mt={2} className={classes.titlePositionDesktop}>
									<div
										style={{
											display: "flex",
											justifyContent: "space-between",
											alignItems: "center",
										}}
									>
										<h1 style={{ margin: 0 }}>{title}</h1>
										<img
											alt="manufacturer"
											src={icon}
											style={{
												height: 40,
												width: 100,
												padding: 10,
											}}
										/>
									</div>

									<p>Sicherheitslevel: {securityScore} / 10</p>
									<div style={{ maxWidth: 300 }}>
										<BorderLinearProgress
											variant="determinate"
											value={securityScore * 10}
										/>
									</div>
									<ul>
										{previewBulletPoints.map((previewBulletPoint) => (
											<li key={previewBulletPoint}>{previewBulletPoint}</li>
										))}
									</ul>

									{longSeoText && (
										<>
											<Box
												my={3}
												p={3}
												style={{
													boxShadow: "0px 0px 5px 0px rgba(0,0,0,0.15)",
													height: !isOpen ? 270 : "auto",
													overflow: !isOpen ? "hidden" : "visible",
												}}
												dangerouslySetInnerHTML={{ __html: longSeoText }}
											></Box>
											<Box style={{ width: 250 }}>
												<Button
													color="link-no-underline"
													onClick={toggleIsOpen}
												>
													{!isOpen ? "Weiterlesen" : "Weniger anzeigen"}{" "}
													<Icon
														path={
															!isOpen ? mdiChevronDown : mdiChevronUp
														}
														size={1}
														color="black"
													/>
												</Button>
											</Box>
										</>
									)}

									<Box my={2}>
										<Link href={`/locking-system/configure/${slug}`}>
											<Button
												color="primary"
												aria-label="to-product"
												size="small"
												style={{ maxWidth: 200 }}
											>
												System planen
											</Button>
										</Link>
									</Box>
								</Box>
							</Grid>
						</Grid>
					</Grid>
				</Grid>
			</Container>

			<Box py={5} mt={4} style={{ background: "#F7F9FA" }}>
				<Container>
					<Grid container spacing={3}>
						<Grid item xs={12} md={8}>
							{lockingSystemComponents.map((component) => {
								return (
									<LockingSystemComponent
										key={component.id}
										component={component}
										name={`${name} ${component.name}`}
										countryCode={countryCode}
										taxRate={taxRate}
									/>
								)
							})}
						</Grid>
						<Grid item xs={12} md={4}>
							<Box
								style={{
									backgroundColor: "#222",
									color: "#fff",
									borderRadius: "4px",
									position: "sticky",
									top: 10,
								}}
								px={2}
								py={2}
							>
								<div>
									<strong>{title}</strong>
								</div>

								<Box mt={2} mb={3}>
									<div>Sicherheitslevel: {securityScore} / 10</div>
									<BorderLinearProgress
										variant="determinate"
										value={securityScore * 10}
									/>
								</Box>
								<Link href={`/locking-system/configure/${slug}`}>
									<Button color="primary" aria-label="to-product" size="small">
										System planen
									</Button>
								</Link>
							</Box>
						</Grid>
					</Grid>
				</Container>
			</Box>
		</>
	)
}

/*
interface StaticPath {
	params: {
		slug: string
		locale: string
	}
}


export const getStaticPaths: GetStaticPaths = async () => {
	const accessToken = await anonymousAccessTokenCache()
	const lockingSystemCategorySlugs = await fetchLockingSystemCategorySlugs(accessToken)

	const paths: StaticPath[] = []

	for (const locale of supportedValidLocales) {
		for (const slug of lockingSystemCategorySlugs) {
			const path: StaticPath = {
				params: {
					locale,
					slug,
				},
			}
			paths.push(path)
		}
	}

	return {
		paths,
		fallback: false,
	}
}

export const getStaticProps: GetStaticProps = async (ctx) => {
	const { slug, locale: rawLocale } = ctx.params as StaticPath["params"]

	// isStrangeVercelParsingError  is when Vercel parses params as eg: { locale: [ 'de-DE', 'locking-system', 'evva-ics' ] }
	const isStrangeVercelParsingError = Array.isArray(rawLocale) && rawLocale.length === 3
	console.info(ctx.params, isStrangeVercelParsingError)

	const { locale, language, countryCode } = normalizeLocale(
		isStrangeVercelParsingError ? rawLocale[0] : rawLocale,
	)
	const key = isStrangeVercelParsingError ? rawLocale[2] : `dzn-sa-${slug}`

	const accessToken = await anonymousAccessTokenCache()
	const lockingSystemCategory = await fetchLockingSystemCategory(accessToken, key, locale)

	const props: Props = {
		locale,
		language,
		countryCode,
		lockingSystemCategory,
	}

	return { props, revalidate: 60 }
}*/

export const getServerSideProps: GetServerSideProps = async (ctx) => {
	const { slug, locale: rawLocale } = ctx.params as {
		slug: string
		locale: string
	}

	// isStrangeVercelParsingError  is when Vercel parses params as eg: { locale: [ 'de-DE', 'locking-system', 'evva-ics' ] }
	const isStrangeVercelParsingError = Array.isArray(rawLocale) && rawLocale.length === 3

	const { locale, language, countryCode } = normalizeLocale(
		isStrangeVercelParsingError ? rawLocale[0] : rawLocale,
	)
	const key = isStrangeVercelParsingError ? rawLocale[2] : `dzn-sa-${slug}`

	const accessToken = await anonymousAccessTokenCache()
	const lockingSystemCategory = await fetchLockingSystemCategory(accessToken, key, locale, "EUR")

	const props: Props = {
		locale,
		language,
		countryCode,
		lockingSystemCategory,
	}

	return { props }
}

interface Props {
	locale: string
	language: string
	countryCode: CountryCode
	lockingSystemCategory: LockingSystemCategory
	currencyCode?: CurrencyCode
}

export default LockingSystemPage
